// Which inventory records a PO should create when it is acknowledged.
//
// Acknowledging a PO can create one stock record per unit ordered, and that was the
// preselected choice for every PO whatever its size. It is the right default for
// serialised kit, where a laptop wants its own asset tag, warranty date and service
// history. It is the wrong one for a consumable bought by the thousand: a single
// line of 2,000 sticky dots produced 2,000 asset-tagged records with one click.
//
// Past the threshold the modal starts on a batch record per line instead. Individual
// is still available, it just has to be chosen deliberately.

// Above this many units, one record per unit stops being a sensible default. Twenty
// is roughly where an order stops being countable kit and starts being supplies.
export const INDIVIDUAL_RECORD_THRESHOLD = 20;

// How many records "create individually" would produce. A line with no usable
// quantity counts as one, matching what the acknowledge handler actually creates.
export function totalUnits(lines) {
  return (lines || []).reduce((sum, line) => sum + Math.max(1, parseInt(line?.quantity, 10) || 1), 0);
}

export function defaultInventoryMode(lines) {
  return totalUnits(lines) > INDIVIDUAL_RECORD_THRESHOLD ? 'batch' : 'individual';
}

// Choosing a record per unit on a large order is a decision rather than an accident,
// so it is confirmed. The other modes create at most one record per line and need no
// guard however big the order is.
export function needsIndividualConfirm(mode, lines) {
  return mode === 'individual' && totalUnits(lines) > INDIVIDUAL_RECORD_THRESHOLD;
}

// Which PO lines have no stock record at all.
//
// Recording a delivery works off inventory records, not off the lines, so a PO
// acknowledged with "Don't create inventory records" — or one whose lines were
// added after acknowledgement, which creates none — has nothing to receive and no
// way back. These are the lines a backfill would create records for.
//
// Matched on the line's description against the record's name, which is what the
// acknowledge handler writes and how the consolidate pass finds a line's records.
// Two lines sharing a description therefore count as covered together: erring
// towards leaving a line out beats creating the same goods in the register twice.
export function linesMissingStock(lines, inventory) {
  const held = new Set((inventory || []).map(i => i?.name));
  return (lines || []).filter(line => !held.has(line?.description));
}
